// AudioAPI Vuex store module

import { isAIFeaturesEnabled } from '@/utils/environmentCheck';

const API_BASE_URL = process.env.VITE_API_BASE_URL || "https://dental-summary-backend-59629711348.asia-southeast1.run.app/api";

const state = {
  isProcessing: false,
  lastTranscription: null,
  error: null,
};

const getters = {
  isProcessing: (state) => state.isProcessing,
  lastTranscription: (state) => state.lastTranscription,
  error: (state) => state.error,
};

const mutations = {
  SET_PROCESSING(state, status) {
    state.isProcessing = status;
  },
  SET_LAST_TRANSCRIPTION(state, transcription) {
    state.lastTranscription = transcription;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // Upload audio file and get streaming response
  async processAudioStream({ commit }, { audioFile, onChunk, onError }) {
    // Disable AI features in production environment
    if (!isAIFeaturesEnabled()) {
      console.warn('AI transcription is disabled in production environment');
      onError('AI transcription is not available in this environment');
      return;
    }

    commit('SET_PROCESSING', true);
    commit('CLEAR_ERROR');
    
    try {
      const formData = new FormData();
      formData.append("audio", audioFile);

      const response = await fetch(`${API_BASE_URL}/process-audio-stream`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              onChunk(data);
              if (data.type === 'complete') {
                commit('SET_LAST_TRANSCRIPTION', data);
              }
            } catch (err) {
              console.warn("Failed to parse chunk:", line);
            }
          }
        }
      }
    } catch (error) {
      console.error("Stream processing error:", error);
      commit('SET_ERROR', error.message);
      onError(error.message);
    } finally {
      commit('SET_PROCESSING', false);
    }
  },

  // Process base64 audio data with streaming
  async processAudioBase64Stream({ commit }, { audioData, fileName, mimeType, onChunk, onError }) {
    // Disable AI features in production environment
    if (!isAIFeaturesEnabled()) {
      console.warn('AI transcription is disabled in production environment');
      onError('AI transcription is not available in this environment');
      return;
    }

    commit('SET_PROCESSING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await fetch(`${API_BASE_URL}/process-audio-base64-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          audioData,
          fileName,
          mimeType,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              onChunk(data);
              if (data.type === 'complete') {
                commit('SET_LAST_TRANSCRIPTION', data);
              }
            } catch (err) {
              console.warn("Failed to parse chunk:", line);
            }
          }
        }
      }
    } catch (error) {
      commit('SET_ERROR', error.message);
      onError(error.message);
    } finally {
      commit('SET_PROCESSING', false);
    }
  },

  // Health check
  async healthCheck({ commit }) {
    try {
      commit('CLEAR_ERROR');
      const response = await fetch(`${API_BASE_URL}/health`);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Health check failed:", error);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};